import {
    createUrl,
    getCookie,
    isProdEnv,
} from '@vfde-sails/utils';
import { debugInfo } from './debug';
import {
    JarvisProduct,
    QueryParams,
} from '../interface';
import {
    BasketPath,
    Cookies,
    Domains,
    ProductType,
    QueryNames,
} from '../enum';
import {
    getCMSConfig,
    isYoungPromoActive,
} from './cms';

const getQueryString = (data: QueryParams) => {
    const params = new URLSearchParams();

    for (const [key, value] of Object.entries(data)) {
        value ? params.set(key, value.toString()) : params.delete(key);
    }

    return params.toString() ? `?${params}` : '';
};

/**
 * Construct url with query params
 *
 * @deprecated use createUrl() from sails-utils-library, * see more https://github.vodafone.com/VFDE-Digital/sails-utils-library/blob/develop/src/url.ts#L93
 * @param url URL.
 * @param query Query params for the URL.
 * @returns URL string.
 */
export const getUrl = (url: URL | string, query: QueryParams = {}): string => {
    try {
        const urlWithQuery = new URL(url, location.href);

        if (!isProdEnv()) {
            urlWithQuery.protocol = location.protocol;
            urlWithQuery.host = location.host;
        }

        urlWithQuery.search = getQueryString(query);

        return urlWithQuery.toString();
    }
    catch (error) {
        debugInfo(error, 'Error while constructing URL');

        return '';
    }
};

/**
 * Update the URL shown in the browser
 *
 * @param isGigakombi - is gigakombi
 * @param selectedTariffId - selected tariff
 */
export const updatePageUrl = (isGigakombi: boolean, selectedTariffId: string) => {
    try {
        const url = new URL(window.location.href);

        url.searchParams.set(QueryNames.PRODUCT, selectedTariffId);

        if (isGigakombi) {
            url.searchParams.set(QueryNames.GIGAKOMBI, isGigakombi.toString());
        }
        else {
            url.searchParams.delete(QueryNames.GIGAKOMBI);
        }

        history.replaceState(null, '', url.toString());
    }
    catch (error) {
        debugInfo('Unable to update page url: ', error);
    }
};

/**
 * Constructs a url to the option page
 * with the given metacode or to the gigakombi
 * mTan check if gigakombi was selected
 *
 * @param tariff Chosen product
 * @param isGigakombi Whether gigakombi was selected
 * @param isYoung If young is selected
 * @param withTvConnect If tv connect is selected
 * @returns A string containg the url
 */
export const getOptionPageUrl = (tariff: JarvisProduct, isGigakombi: boolean, isYoung: boolean, withTvConnect = false) => {
    const { id, youngDiscount, youngPromoDiscount } = tariff;

    const queryParams: QueryParams = {
        [QueryNames.BASKET]: id,
        [QueryNames.FF]: getCookie(Cookies.FF),
        ...!withTvConnect && {
            [QueryNames.CABLE_ACCESS]: 'false',
        },
        ...isYoung && isYoungPromoActive() && youngPromoDiscount && {
            [QueryNames.OPTION_BASKET]: youngPromoDiscount.ids.join(','),
        },
        ...isYoung && !isYoungPromoActive() && youngDiscount && youngDiscount.ids && {
            [QueryNames.OPTION_BASKET]: youngDiscount.ids.join(','),
        },
    };

    if (!isGigakombi) {
        return getUrl(Domains.ZUHAUSEPLUS + BasketPath.OPTION, queryParams);
    }

    const contentUrl = getCMSConfig().contentUrls;
    const gigakombiQueryParams: QueryParams = {
        [QueryNames.TARGET]: createUrl(Domains.ZUHAUSEPLUS, BasketPath.OPTION, queryParams),
    };

    return getUrl(Domains.VF + contentUrl.mtan, gigakombiQueryParams);
};

/**
 * Constructs a url to the product landing pag
 * with the selected metacode and ff token if exists
 *
 * @param id Metacode of the product
 * @param productType Product type of the product
 * @param isGigakombi Gigakombi selection
 * @param isYoung Young selection
 * @returns A string containg the url
 */
export const getProductPageUrl = (
    id: string | undefined,
    productType: ProductType | undefined,
    isGigakombi: boolean,
    isYoung: boolean,
) => {
    const queryParams: QueryParams = {
        [QueryNames.PRODUCT]: id,
        [QueryNames.FF]: getCookie(Cookies.FF),
        ...isGigakombi && {
            [QueryNames.GIGAKOMBI]: isGigakombi,
        },
    };

    const { contentUrls } = getCMSConfig();

    switch (productType) {
        case ProductType.CABLE:
            if (id === getCMSConfig().selectedTariffId) {
                delete queryParams[QueryNames.PRODUCT];
            }

            return getUrl(isYoung ? contentUrls.cableYoung : contentUrls.cable, queryParams);
        case ProductType.FIBRE:
            return getUrl(contentUrls.fiber, queryParams);
        case ProductType.DSL:
            if (isYoung) {
                queryParams[QueryNames.YOUNG] = isYoung;
            }

            return getUrl(contentUrls.dsl, queryParams);
        default:
            if (isYoung) {
                queryParams[QueryNames.YOUNG] = isYoung;
            }

            delete queryParams[QueryNames.GIGAKOMBI];
            delete queryParams[QueryNames.PRODUCT];

            return getUrl(contentUrls.gigacube, queryParams);
    }
};

/**
 * Returns true if customer is in 3P case based on the URL checks
 */
export const isCustomerIn3pFlowByUrl = (): boolean => {
    const url = new URL(location.href);

    if (!url.searchParams.get(QueryNames.THREE_P)) {
        return false;
    }

    // Remove from URL to prevent bookmarking
    url.searchParams.delete(QueryNames.THREE_P);
    history.replaceState(history.state, '', url);

    return is3pReferrerMatches();
};

/**
 * Checks whether the customer is coming from the 3p LandingPage directly
 *
 * Note 1: Referrer cannot be tested on http addresses like localhost,
 * therefore on localhost this function always returns true
 *
 * Note 2: CMS Config always stores URLs as '/example/page.html', without the origin,
 * to keep it consistent, this function handles that and concatenates the location.origin to it
 */
export const is3pReferrerMatches = (): boolean => isProdEnv()
    ? document.referrer === `${location.origin}${getCMSConfig().landingPage3pUrl}`
    : true;
